import { syncDeleteRemote } from './api-operations';
import { isLotClosed } from './utils';
import { Lot, State } from './types';
import { isDeadMeat } from './lotOperations/financials';
import { createLogger } from '../../shared/logger.js';
import { extractErrorMessage } from '../../shared/errorUtils.js';
import { buildRemovalSelection, canArchiveUrls } from './bulkOperations/selection';
import {
  notifyArchiveFailure,
  notifyArchiveSuccess,
  notifyDeleteFailure,
  notifyDeleteSuccess,
  notifyRemovalError,
  notifyRemovalFailures,
  notifyRemovalSuccess,
  promptArchiveUrls,
} from './bulkOperations/ui';
import { RemoveLotsFlagKey, removeSingleLot, runRemoval } from './bulkOperations/removalService';

const logBulk = createLogger('portal:bulk');

interface HandleRemovalOptions {
  event: Event | null;
  state: State;
  render: () => void;
  save: () => void;
  applyRemoteSnapshot: (lots: Lot[]) => void;
  ensureAccessToken: () => Promise<string | null>;
  DEBUG: boolean;
  getTargetLots: () => Lot[];
  runningFlagKey: RemoveLotsFlagKey;
  successMessage: (count: number) => string;
  failureLabelSingular: string;
  failureLabelPlural: string;
  failurePrefix: string;
}

export function handleArchiveUrls(
  state: State,
  render: () => void,
  __archiveUrl: (url: string) => void,
  promptFn?: (message: string, defaultValue?: string) => string | null
): void {
  if (!canArchiveUrls(state)) {
    return;
  }

  try {
    const urls = promptArchiveUrls(promptFn);
    if (!urls.length) {
      return;
    }

    let archivedCount = 0;
    urls.forEach(url => {
      try {
        __archiveUrl(url);
        archivedCount++;
      } catch (err) {
        logBulk.debug('Failed to archive URL', {
          url,
          error: extractErrorMessage(err),
        });
      }
    });

    if (archivedCount > 0) {
      render();
      notifyArchiveSuccess(archivedCount);
    }
  } catch (err) {
    logBulk.error('handleArchiveUrls failed', err);
    notifyArchiveFailure(err);
  }
}

async function handleRemoval(options: HandleRemovalOptions): Promise<void> {
  const {
    event,
    state,
    render,
    save,
    applyRemoteSnapshot,
    ensureAccessToken,
    DEBUG,
    getTargetLots,
    runningFlagKey,
    successMessage,
    failureLabelSingular,
    failureLabelPlural,
    failurePrefix,
  } = options;

  if (event && typeof event.preventDefault === 'function') {
    event.preventDefault();
  }

  const targetLots = Array.isArray(state.lots) ? getTargetLots() : [];
  const selection = buildRemovalSelection(state, targetLots);

  const result = await runRemoval({
    selection,
    runningFlagKey,
    state,
    render,
    save,
    applyRemoteSnapshot,
    ensureAccessToken,
    DEBUG,
    deleteRemote: syncDeleteRemote,
  });

  if (result.skipped) {
    return;
  }

  if (result.error) {
    notifyRemovalError(failurePrefix, result.error);
    return;
  }

  if (result.remoteErrors.length) {
    notifyRemovalFailures(result.remoteErrors, failureLabelSingular, failureLabelPlural);
  }

  notifyRemovalSuccess(result.removedCount, successMessage);
}

export async function handleRemoveClosedLots(
  event: Event | null,
  state: State,
  getEffectiveCloseTime: (lot: Lot) => number,
  render: () => void,
  save: () => void,
  applyRemoteSnapshot: (lots: Lot[]) => void,
  ensureAccessToken: () => Promise<string | null>,
  DEBUG: boolean
): Promise<void> {
  return handleRemoval({
    event,
    state,
    render,
    save,
    applyRemoteSnapshot,
    ensureAccessToken,
    DEBUG,
    getTargetLots: () => state.lots.filter(lot => isLotClosed(lot, getEffectiveCloseTime)),
    runningFlagKey: 'removingClosedLots',
    successMessage: count =>
      count === 1 ? 'Removed 1 closed lot.' : `Removed ${count} closed lots.`,
    failureLabelSingular: 'closed lot',
    failureLabelPlural: 'closed lots',
    failurePrefix: 'Failed to remove closed lots',
  });
}

export async function handleRemoveDeadMeat(
  event: Event | null,
  state: State,
  ensureSettings: () => any,
  render: () => void,
  save: () => void,
  applyRemoteSnapshot: (lots: Lot[]) => void,
  ensureAccessToken: () => Promise<string | null>,
  DEBUG: boolean
): Promise<void> {
  const settings = ensureSettings();

  return handleRemoval({
    event,
    state,
    render,
    save,
    applyRemoteSnapshot,
    ensureAccessToken,
    DEBUG,
    getTargetLots: () => state.lots.filter(lot => isDeadMeat(lot, settings)),
    runningFlagKey: 'removingDeadMeat',
    successMessage: count =>
      count === 1 ? 'Removed 1 unprofitable lot.' : `Removed ${count} unprofitable lots.`,
    failureLabelSingular: 'unprofitable lot',
    failureLabelPlural: 'unprofitable lots',
    failurePrefix: 'Failed to remove unprofitable lots',
  });
}

export function handleDeleteLot(
  lot: Lot,
  state: State,
  render: () => void,
  applyRemoteSnapshot: (lots: Lot[]) => void,
  ensureAccessToken: () => Promise<string | null>,
  DEBUG: boolean
): void {
  if (!lot || !lot.id) {
    return;
  }

  const titleForToast = lot.title ? `"${lot.title}"` : 'Lot';

  void removeSingleLot({
    lot,
    state,
    render,
    applyRemoteSnapshot,
    ensureAccessToken,
    DEBUG,
    deleteRemote: syncDeleteRemote,
  })
    .then(result => {
      if (result.removed) {
        notifyDeleteSuccess(titleForToast);
        return;
      }

      if (result.error) {
        notifyDeleteFailure(titleForToast, result.error);
        logBulk.error('Failed to delete lot', result.error);
      }
    })
    .catch(err => {
      notifyDeleteFailure(titleForToast, err);
      logBulk.error('Failed to delete lot', err);
    });
}
